/**
 * @fileoverview View Mode Context for Internal/Audit View Switching
 * @description Provides dual interface capability - Internal view shows all data including
 * corrections, adjustments, and internal notes. Audit view shows only official/sanitized data.
 * Audit mode is persisted in DB and controlled by super_admin only.
 */

import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { useAuthContext } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

export type ViewMode = 'internal' | 'audit';

interface ViewModeContextType {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  isInternalView: boolean;
  isAuditView: boolean;
  canSwitchView: boolean;
  isLoading: boolean;
}

const ViewModeContext = createContext<ViewModeContextType | undefined>(undefined);

interface ViewModeProviderProps {
  children: ReactNode;
}

const SETTING_KEY = 'audit_view_enabled';

/**
 * View Mode Provider Component
 * Controls whether the system displays internal (full) or audit (sanitized) data.
 * Audit mode is a system-wide setting stored in DB, toggled only by super_admin.
 * All users see the current mode status.
 *
 * Internal View (Default):
 * - Shows attendance corrections with reasons
 * - Displays unofficial overtime and internal bonuses
 * - Contains employee internal notes and verbal warnings
 * - Shows salary adjustments and informal arrangements
 *
 * Audit View (For External Auditors/Ministry of Labor):
 * - Shows only processed/final attendance records
 * - Displays only official salary per contract
 * - Shows only approved overtime per labor law limits
 * - Contains only official written warnings
 * - Hides correction reasons, internal notes, manual entry flags
 */
export function ViewModeProvider({ children }: ViewModeProviderProps) {
  const { role } = useAuthContext();
  const [viewMode, setViewModeState] = useState<ViewMode>('internal');
  const [isLoading, setIsLoading] = useState(true);

  // Only super_admin can switch views
  const canSwitchView = role === 'super_admin';

  // Fetch current audit mode from DB on mount
  useEffect(() => {
    const fetchAuditMode = async () => {
      try {
        const { data, error } = await supabase
          .from('payroll_settings')
          .select('setting_value')
          .eq('setting_key', SETTING_KEY)
          .maybeSingle();

        if (error) {
          logger.error('Error fetching audit mode setting', 'ViewMode', error);
        } else if (data?.setting_value) {
          const value = data.setting_value as { enabled: boolean };
          setViewModeState(value.enabled ? 'audit' : 'internal');
        }
      } catch (err) {
        logger.error('Failed to fetch audit mode', 'ViewMode', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAuditMode();
  }, []);

  // Subscribe to realtime changes on the setting (only when authenticated)
  useEffect(() => {
    if (!role) return;

    const channel = supabase
      .channel('audit-mode-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payroll_settings',
          filter: `setting_key=eq.${SETTING_KEY}`,
        },
        (payload: any) => {
          const newValue = payload.new?.setting_value as { enabled: boolean } | undefined;
          if (newValue !== undefined) {
            setViewModeState(newValue.enabled ? 'audit' : 'internal');
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [role]);

  const handleSetViewMode = useCallback(async (mode: ViewMode) => {
    // Only super_admin can toggle
    if (role !== 'super_admin') return;

    const previousMode = viewMode;
    setViewModeState(mode); // Optimistic update

    try {
      // Upsert the setting in DB
      const { error } = await supabase
        .from('payroll_settings')
        .upsert(
          {
            setting_key: SETTING_KEY,
            setting_value: { enabled: mode === 'audit' } as any,
            description: 'System-wide audit view mode toggle',
          },
          { onConflict: 'setting_key' }
        );

      if (error) {
        logger.error('Error saving audit mode', 'ViewMode', error);
        setViewModeState(previousMode); // Rollback
      }
    } catch (err) {
      logger.error('Failed to save audit mode', 'ViewMode', err);
      setViewModeState(previousMode); // Rollback
    }
  }, [role, viewMode]);

  return (
    <ViewModeContext.Provider
      value={{
        viewMode,
        setViewMode: handleSetViewMode,
        isInternalView: viewMode === 'internal',
        isAuditView: viewMode === 'audit',
        canSwitchView,
        isLoading,
      }}
    >
      {children}
    </ViewModeContext.Provider>
  );
}

/**
 * Hook to access view mode context
 */
export function useViewMode(): ViewModeContextType {
  const context = useContext(ViewModeContext);

  if (context === undefined) {
    throw new Error('useViewMode must be used within a ViewModeProvider');
  }

  return context;
}
